/**
 * Contact Flow Summarization Service
 *
 * Provides summarization capabilities using Amazon Bedrock (Nova Micro).
 * Simplified synchronous API - no polling, no job tracking.
 */

import axios from 'axios';

/**
 * Configuration for summarization service
 */
const SUMMARIZATION_CONFIG = {
  // API endpoint - can be overridden by environment variable
  apiUrl: process.env.REACT_APP_SUMMARIZATION_API_URL || null,

  // Request timeout (Bedrock typically responds in 1-3 seconds)
  timeout: 30000, // 30 seconds (generous buffer)

  // Retry configuration. Under higher client concurrency, Bedrock throttles surface as 5xx/429,
  // so retry uses exponential backoff + jitter (retryDelay is the base; capped at maxRetryDelay).
  maxRetries: 4,
  retryDelay: 1000, // base backoff (ms)
  maxRetryDelay: 8000, // cap for exponential backoff (ms)

  // Request headers
  headers: {
    'Content-Type': 'application/json',
  }
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get the summarization API URL from config or environment
 */
const getSummarizationApiUrl = () => {
  const url = SUMMARIZATION_CONFIG.apiUrl;

  if (!url) {
    throw new Error(
      'Summarization API URL not configured. ' +
      'Please set REACT_APP_SUMMARIZATION_API_URL environment variable.'
    );
  }

  return url;
};

/**
 * Check if summarization is available
 */
const checkSummarizationAvailability = () => {
  try {
    getSummarizationApiUrl();
    return true;
  } catch (error) {
    console.warn('Summarization not available:', error.message);
    return false;
  }
};

/**
 * Extract error message from axios error or generic error
 */
const extractErrorMessage = (error) => {
  if (error.response) {
    // API returned an error response
    const data = error.response.data;
    return data.error || data.details || data.message || 'Unknown API error';
  } else if (error.request) {
    // Request made but no response
    return 'No response from summarization service (network error)';
  } else {
    // Other error
    return error.message || 'Unknown error';
  }
};

/**
 * Extract all resource IDs (UUIDs and ARNs) referenced in a contact flow JSON.
 * Recursively walks the flow structure to find all resources used.
 *
 * @param {object} flowJson - Amazon Connect flow JSON
 * @returns {Set<string>} Set of unique resource IDs found in the flow
 */
const extractUsedResourceIds = (flowJson) => {
  const resourceIds = new Set();

  // UUID pattern (8-4-4-4-12 format)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // ARN pattern (includes resource IDs at the end)
  const arnRegex = /arn:aws:[^:]+:[^:]+:[^:]+:[^/]+\/(.+)$/;

  /**
   * Recursively walk object/array and extract resource IDs
   */
  const walk = (obj) => {
    if (obj === null || obj === undefined) {
      return;
    }

    if (typeof obj === 'string') {
      // Check if it's a UUID
      if (uuidRegex.test(obj)) {
        resourceIds.add(obj);
      }
      // Check if it's an ARN and extract the resource ID
      const arnMatch = obj.match(arnRegex);
      if (arnMatch && arnMatch[1]) {
        // Extract the last part of the ARN which is usually the resource ID
        const resourceId = arnMatch[1].split('/').pop();
        if (uuidRegex.test(resourceId)) {
          resourceIds.add(resourceId);
        }
      }
    } else if (Array.isArray(obj)) {
      obj.forEach(item => walk(item));
    } else if (typeof obj === 'object') {
      Object.values(obj).forEach(value => walk(value));
    }
  };

  walk(flowJson);
  return resourceIds;
};

/**
 * Create resource name mapping from exportedData for Lambda lookups.
 * Maps resource IDs to friendly names to avoid sending large exportedData payload.
 * Optionally filters to only include resources used in a specific flow.
 *
 * @param {object} exportedData - Full exported AWS Connect data
 * @param {string} instanceId - Connect instance ID
 * @param {object} flowJson - Optional flow JSON to filter resources (only includes resources used in this flow)
 * @returns {object} Resource name mapping by type
 */
const createResourceNameMap = (exportedData, instanceId, flowJson = null) => {
  const instanceData = exportedData?.[instanceId];
  if (!instanceData) {
    console.warn(`No instance data found for ${instanceId}, skipping resource name mapping`);
    return {};
  }

  // Extract resource IDs used in the flow if flowJson is provided
  let usedResourceIds = null;
  if (flowJson) {
    usedResourceIds = extractUsedResourceIds(flowJson);
    console.log(`Flow uses ${usedResourceIds.size} unique resource IDs`);
  }

  // Helper to map array of resources to ID->Name mapping
  // If usedResourceIds is provided, only include resources that are used
  const mapArray = (arr, nameKey = 'Name') =>
    (arr || []).reduce((map, item) => {
      if (item.Id && item[nameKey]) {
        // If filtering, only include resources that are actually used
        if (!usedResourceIds || usedResourceIds.has(item.Id)) {
          map[item.Id] = item[nameKey];
        }
      }
      return map;
    }, {});

  const mapping = {
    queues: mapArray(instanceData.queues),
    prompts: mapArray(instanceData.prompts),
    hoursOfOperation: mapArray(instanceData.hoursOfOperation),
    contactFlows: mapArray(instanceData.contactFlows),
    contactFlowModules: mapArray(instanceData.contactFlowModules),
    users: mapArray(instanceData.users, 'Username'),
    lambdaFunctions: mapArray(instanceData.lambdaFunctions, 'FunctionName')
  };

  const totalMappings = Object.values(mapping).reduce((sum, map) => sum + Object.keys(map).length, 0);
  const filterStatus = flowJson ? ' (filtered to flow resources)' : ' (all resources)';
  console.log(`Created resource name mapping with ${totalMappings} entries${filterStatus}`);

  return mapping;
};

/**
 * Strip Metadata from flow JSON to reduce token count.
 * Metadata contains only visual positioning data for AWS Connect UI
 * (entryPointPosition, ActionMetadata, Annotations) which is not needed
 * for summarization and wastes tokens.
 *
 * @param {object} flowJson - Amazon Connect flow JSON
 * @returns {object} Flow JSON without Metadata
 */
const stripMetadata = (flowJson) => {
  // Create a deep copy to avoid mutating the original
  const cleanedFlow = JSON.parse(JSON.stringify(flowJson));

  // Remove entire Metadata object - it's purely visual layout information
  if (cleanedFlow.Metadata) {
    delete cleanedFlow.Metadata;
    console.log('Stripped Metadata from flow JSON (visual layout data not needed for summarization)');
  }

  return cleanedFlow;
};

/**
 * Summarize a single contact flow using Bedrock (synchronous)
 *
 * @param {string} flowName - Name of the contact flow
 * @param {object} flowJson - Amazon Connect flow JSON
 * @param {object} options - Optional configuration
 * @param {object} options.exportedData - Full exported data for resource name lookups
 * @param {string} options.instanceId - Connect instance ID for resource lookups
 * @param {string} options.flowType - Contact flow type (CONTACT_FLOW, AGENT_WHISPER, etc.)
 * @param {function} options.onProgress - Progress callback (not used, kept for compatibility)
 * @returns {Promise<object>} Summary result
 */
const summarizeContactFlow = async (flowName, flowJson, options = {}) => {
  // Validate inputs
  if (!flowName) {
    throw new Error('Flow name is required for summarization');
  }

  if (!flowJson || typeof flowJson !== 'object') {
    throw new Error('Valid flow JSON is required for summarization');
  }

  const apiUrl = getSummarizationApiUrl();
  let lastError = null;

  // Strip Metadata to reduce token count (visual layout data not needed)
  const cleanedFlowJson = stripMetadata(flowJson);

  // Create resource name mapping if exportedData provided
  // Pass cleanedFlowJson to filter mapping to only resources used in this specific flow
  const resourceNameMap = options.exportedData && options.instanceId
    ? createResourceNameMap(options.exportedData, options.instanceId, cleanedFlowJson)
    : {};

  // Retry logic for network issues
  for (let attempt = 1; attempt <= SUMMARIZATION_CONFIG.maxRetries; attempt++) {
    try {
      const startTime = Date.now();

      console.log(`[${attempt}/${SUMMARIZATION_CONFIG.maxRetries}] Requesting summary for: ${flowName}`);

      // Single synchronous POST request to Bedrock-based API
      // Build headers with optional auth token
      const headers = { ...SUMMARIZATION_CONFIG.headers };
      if (options.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }

      const response = await axios.post(
        apiUrl,
        {
          flowName,
          flowJson: cleanedFlowJson,  // Use cleaned version (Metadata stripped)
          resourceNameMap,  // Include resource name mapping for friendly names
          modelId: options.selectedModel,  // User-selected Bedrock model
          flowType: options.flowType || 'CONTACT_FLOW'  // Contact flow type for context-aware summarization
        },
        {
          timeout: SUMMARIZATION_CONFIG.timeout,
          headers
        }
      );

      const duration = Date.now() - startTime;
      const bedrockDuration = response.data.metadata?.durationMs || 'N/A';

      console.log(`✅ Summary received in ${(duration / 1000).toFixed(2)}s (Bedrock: ${bedrockDuration}ms)`);

      // Return result
      return {
        success: true,
        flowName: response.data.flowName,
        summary: response.data.summary || [],
        metadata: {
          ...response.data.metadata,
          totalDuration: duration
        }
      };

    } catch (error) {
      lastError = error;
      const errorMsg = extractErrorMessage(error);

      console.error(`❌ Attempt ${attempt} failed for ${flowName}:`, errorMsg);

      // Non-retryable 4xx client errors (except 429 Too Many Requests, which is throttling).
      // Retryable: network errors, 5xx server errors (incl. Bedrock throttles re-raised as 500),
      // and 429.
      if (error.response &&
          error.response.status >= 400 &&
          error.response.status < 500 &&
          error.response.status !== 429) {
        throw new Error(`Failed to summarize flow "${flowName}": ${errorMsg}`);
      }

      // Retry with exponential backoff + jitter to avoid a thundering herd under concurrency
      if (attempt < SUMMARIZATION_CONFIG.maxRetries) {
        const backoff = Math.min(
          SUMMARIZATION_CONFIG.maxRetryDelay,
          SUMMARIZATION_CONFIG.retryDelay * Math.pow(2, attempt - 1)
        );
        const delay = backoff + Math.floor(Math.random() * 250);
        console.log(`Retrying in ${delay}ms...`);
        await sleep(delay);
      }
    }
  }

  // All retries failed
  const errorMessage = extractErrorMessage(lastError);
  throw new Error(`Failed to summarize flow "${flowName}" after ${SUMMARIZATION_CONFIG.maxRetries} attempts: ${errorMessage}`);
};

/**
 * Summarize multiple contact flows sequentially
 *
 * @param {Array<object>} flows - Array of {name, json} objects
 * @param {function} onProgress - Progress callback (flowName, current, total)
 * @returns {Promise<object>} Results map
 */
const summarizeMultipleFlows = async (flows, onProgress = null) => {
  console.log(`Starting batch summarization of ${flows.length} flows...`);

  const results = {};

  for (let i = 0; i < flows.length; i++) {
    const flow = flows[i];
    const flowName = flow.name;

    try {
      // Call progress callback
      if (onProgress) {
        onProgress(flowName, i + 1, flows.length);
      }

      // Summarize flow
      const result = await summarizeContactFlow(flowName, flow.json);
      results[flowName] = result;

      console.log(`✅ [${i + 1}/${flows.length}] Completed: ${flowName}`);

    } catch (error) {
      console.error(`❌ [${i + 1}/${flows.length}] Failed: ${flowName}`, error.message);

      // Store error result
      results[flowName] = {
        success: false,
        flowName,
        error: error.message,
        summary: []
      };
    }
  }

  console.log(`Batch summarization complete. Success: ${Object.values(results).filter(r => r.success).length}/${flows.length}`);

  return results;
};

/**
 * Format summary for display in UI
 */
const formatSummary = (summary) => {
  if (!summary || !Array.isArray(summary) || summary.length === 0) {
    return 'No summary available';
  }

  return summary.map((point, index) => `${index + 1}. ${point}`).join('\n');
};

/**
 * Format summary for Excel export
 */
const formatSummaryForExcel = (summary) => {
  if (!summary || !Array.isArray(summary) || summary.length === 0) {
    return 'No summary available';
  }

  // Join with line breaks for Excel
  return summary.map((point, index) => `${index + 1}. ${point}`).join('\n');
};

// Create service object
const summarizationService = {
  checkSummarizationAvailability,
  summarizeContactFlow,
  summarizeMultipleFlows,
  formatSummary,
  formatSummaryForExcel,
  SUMMARIZATION_CONFIG
};

// Export service functions
export default summarizationService;

// Named exports for convenience
export {
  checkSummarizationAvailability,
  summarizeContactFlow,
  summarizeMultipleFlows,
  formatSummary,
  formatSummaryForExcel,
  SUMMARIZATION_CONFIG
};
